import classNames from 'classnames';
import React, { useState } from 'react';
import { useMutateCard } from '../../../hooks/apolloHooks/useMutateCard';
import { validateCardPin } from '../../../utils/cardValidationSchemas';
import { CheckmarkIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import RightsWrapper from '../RightsWrapper';

type CardPinEditorProps = {
	cardId: string
	isCreditCard?: boolean
	onUpdateSuccess?: (newPin?: string) => void
}

const CardPinEditor: React.FC<CardPinEditorProps> = ({ cardId, isCreditCard, onUpdateSuccess }) => {
	const [isEditing, setIsEditing] = useState(false)
	const [newPin, setNewPin] = useState('')
	const [validationError, setValidationError] = useState<string | null>(null)
	const [successMessage, setSuccessMessage] = useState<string | null>(null)
	const { updateCardPinCode, loading, error } = useMutateCard()

	const handleEditClick = () => {
		setNewPin('')
		setValidationError(null)
		setIsEditing(true)
	}

	const handleCancel = () => {
		setNewPin('')
		setValidationError(null)
		setIsEditing(false)
	}

	const handleSave = async () => {
		try {
			// Validate input
			const validation = validateCardPin(newPin)
			if (!validation.valid) {
				setValidationError(validation.error || 'Felaktig PIN-kod')
				return
			}
			setValidationError(null)

			const result = await updateCardPinCode({
				cardId: parseInt(cardId),
				pinCode: parseInt(newPin)
			})

			if (result.data) {
				setIsEditing(false)
				setNewPin('')
				setSuccessMessage('Pinkod uppdaterad')
				// Delay refetch to allow success message to be shown, then refetch
				setTimeout(() => {
					if (onUpdateSuccess) {
						onUpdateSuccess(newPin)
					}
				}, 1500) // 1.5 second delay before refetch
			}
		} catch (err) {
			console.error('Failed to update card PIN:', err)
			setValidationError('Kunde inte uppdatera PIN-kod. Försök igen.')
		}
	}

	if (!isEditing) {
		return (
			<div className="card-pin-editor">
				<div>
					{successMessage && (
					<p className="card-pin-editor__success">
						<CheckmarkIcon />
						{successMessage}
					</p>
				)}
				<span className="disabled-input">****</span>
				</div>
				<RightsWrapper
					disableButtonText={'Ändra pinkod'}
					disableButtonClasses="disable-fill-color body-text"
				>
					<button
						type="button"
						className={classNames(
							'card-pin-editor__edit-button button-edit body-text',
							{ 'invert': !isCreditCard }
						)}
						onClick={handleEditClick}
					>
						Ändra pinkod
					</button>
				</RightsWrapper>
				
			</div>
		)
	}

	return (
		<div className="card-pin-editor card-pin-editor--editing">
			<label htmlFor={`card-pin-${cardId}`} className="card-pin-editor__label">
				PIN-kod
			</label>
			<input
				id={`card-pin-${cardId}`}
				type="password"
				className="card-pin-editor__input"
				value={newPin}
				onChange={(e) => setNewPin(e.target.value)}
				disabled={loading}
				placeholder="Ange ny PIN-kod"
				maxLength={4}
			/>
			{validationError && (
				<p className="card-pin-editor__error">
					{validationError}
				</p>
			)}
			{error && (
				<p className="card-pin-editor__error">
					Kunde inte uppdatera PIN-kod. Försök igen eller kontakta kundservice.
				</p>
			)}
			<div className="card-pin-editor__actions">
				<Button
					className="body-text"
					type="button"
					onClick={handleSave}
					disabled={loading}
				>
					{loading ? 'Sparar...' : 'Spara'}
				</Button>
				<Button
					type="button"
					className={classNames(
						'button-cancel',
						'body-text',
						{ 'invert': isCreditCard }
					)}
					onClick={handleCancel}
					disabled={loading}
				>
					Avbryt
				</Button>
			</div>
		</div>
	)
}

export default CardPinEditor
